import { Activity, type ComponentProps, startTransition, useEffect, useRef, useState } from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Sidebar, useSidebar } from '@/components/ui/sidebar';
import { getHorizontalSlideClasses } from '@/components/ui/system/transitions';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsVersionHistory } from '@/hooks/use-is-version-history';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import DefaultSidebar from './default-sidebar';
import PageTreeSidebar from './page-tree-sidebar';

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const isPageEditorRoute = useIsPageEditor();
  const isVersionHistory = useIsVersionHistory();
  // Stay on the page tree during version history so accordion open state isn't lost.
  const pageEditorMatch = isPageEditorRoute || isVersionHistory;
  const [isPageEditor, setIsPageEditor] = useState(pageEditorMatch);
  const { isMobile, open, setOpen } = useSidebar();
  const previousPageEditorMatchRef = useRef(pageEditorMatch);
  const editorOpenRef = useRef(open);

  useEffect(() => {
    startTransition(() => {
      setIsPageEditor(pageEditorMatch);
    });
  }, [pageEditorMatch]);

  // Non-editor pages can't collapse — expand while away, remember editor open/closed.
  useIsomorphicLayoutEffect(() => {
    if (isMobile !== false) {
      return;
    }

    const wasPageEditor = previousPageEditorMatchRef.current;

    if (wasPageEditor && !pageEditorMatch) {
      editorOpenRef.current = open;
      setOpen(true);
    } else if (!wasPageEditor && pageEditorMatch && open !== editorOpenRef.current) {
      setOpen(editorOpenRef.current);
    }

    previousPageEditorMatchRef.current = pageEditorMatch;
  }, [isMobile, pageEditorMatch, open, setOpen]);

  // Always offcanvas so peek works and 16↔20rem tweens on a stable DOM (no none remount).
  return (
    <Sidebar {...props} collapsible="offcanvas">
      <CloseSidebarButton />
      <div className={getHorizontalSlideClasses(!isPageEditor, 'left')} inert={isPageEditor}>
        <Activity mode={!isPageEditor ? 'visible' : 'hidden'} name="default-sidebar">
          <DefaultSidebar />
        </Activity>
      </div>
      <div className={getHorizontalSlideClasses(isPageEditor, 'right')} inert={!isPageEditor}>
        <Activity mode={isPageEditor ? 'visible' : 'hidden'} name="page-tree-sidebar">
          <PageTreeSidebar
            key={isPageEditor ? 'page-tree-active' : 'page-tree-inactive'}
            isActive={isPageEditor}
          />
        </Activity>
      </div>
    </Sidebar>
  );
}

function CloseSidebarButton() {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      colorScheme="secondary"
      variant="ghost"
      size="icon"
      className="absolute top-4 right-4 md:hidden"
      onClick={toggleSidebar}
    >
      <Icon icon={faXmark} />
    </Button>
  );
}
